import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import {
  acquirePopupSpinnerLoading,
  releasePopupSpinnerLoading,
  setErrors,
  updateCompareGoalsData,
  updateWhatIfsData,
  updateCompareGoalsPlanImpacts,
  optimizeCompareGoals,
} from '../WhatIfsActions';
import { setPlanImpact } from '../../FinancialPlan/FinancialPlanActions';
import { stageWhatIfs } from '../../MonteCarloChart/MonteCarloChartActions';
import { goalsAdditionalSavingsWhatIfsOrDefaultSelector } from './CompareGoalsSelector';

export const optimizeGoalsResponse = (
  whatIfs,
  dispatch,
  goalsAdditionalSavingsWhatIfs,
  clientId,
  goalId,
  whatIfKey,
  whatIfType,
) => {
  dispatch(optimizeCompareGoals(whatIfs, clientId, goalId, whatIfType, whatIfKey));
};

export const optimizeGoals = (
  clientId,
  fpId,
  goalId,
  whatIfKey,
  whatIfType,
  goalsAdditionalSavingsWhatIfs,
) => dispatch => {
  const { ADV } = window;
  dispatch(acquirePopupSpinnerLoading(clientId));
  ADV.util
    .request({
      type: 'POST',
      uri: 'Consumer/CompareGoals/Optimize',
      urlParams: {
        clientId,
        fpId,
      },
      showSpinner: false,
    })
    .then(response => {
      if (response.success) {
        dispatch(setErrors(clientId, []));
        optimizeGoalsResponse(
          response.payload.viewData,
          dispatch,
          goalsAdditionalSavingsWhatIfs,
          clientId,
          goalId,
          whatIfKey,
          whatIfType,
        );
      } else {
        dispatch(setErrors(clientId, response.errors));
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      () => dispatch(releasePopupSpinnerLoading(clientId)),
      () => dispatch(releasePopupSpinnerLoading(clientId)),
    );
};

export const optimizeGoalsMock = (
  clientId,
  fpId,
  goalId,
  whatIfKey,
  whatIfType,
  goalsAdditionalSavingsWhatIfs,
) => (dispatch, getState) => {
  dispatch(acquirePopupSpinnerLoading(clientId));
  setTimeout(() => {
    dispatch(setErrors(clientId, []));
    optimizeGoalsResponse(
      goalsAdditionalSavingsWhatIfsOrDefaultSelector(getState(), { clientId }).map(
        (whatIf, index) => ({
          ...whatIf.WhatIfPanelItem.WhatIf,
          AllocatedCashflow: 1000 + index,
        }),
      ),
      dispatch,
      goalsAdditionalSavingsWhatIfs,
      clientId,
      goalId,
      whatIfKey,
      whatIfType,
    );

    dispatch(releasePopupSpinnerLoading(clientId));
  }, 500);
};

export const fetchCompareGoals = (clientId, fpId) => dispatch => {
  const { ADV } = window;
  dispatch(acquirePopupSpinnerLoading(clientId));
  ADV.util
    .request({
      uri: 'Consumer/CompareGoals',
      data: {
        clientId,
        fpId,
      },
      showSpinner: false,
    })
    .then(response => {
      if (response.success) {
        dispatch(setErrors(clientId, []));
        const whatIfs = response.payload.viewData;
        // Mutate the data to include the initial what ifs
        whatIfs.GoalsAdditionalSavingsWhatIfs.forEach(whatIfArg => {
          const whatIf = whatIfArg;
          whatIf.WhatIfInitialPanelItem = cloneDeep(whatIf.WhatIfPanelItem);
        });
        dispatch(updateCompareGoalsData(null, whatIfs, clientId));
      } else {
        dispatch(setErrors(clientId, response.errors));
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      () => dispatch(releasePopupSpinnerLoading(clientId)),
      () => dispatch(releasePopupSpinnerLoading(clientId)),
    );
};

export const fetchAdditionalSavingsPlanImpact = (
  clientId,
  fpId,
  goalId,
  whatIfType,
  whatIfKey,
  whatIfs,
) => dispatch => {
  const { ADV } = window;
  const normalizedWhatIfs = whatIfs.map(whatIf => whatIf.WhatIfPanelItem.WhatIf);
  dispatch(stageWhatIfs(clientId, normalizedWhatIfs));
  dispatch(acquirePopupSpinnerLoading(clientId));

  ADV.util
    .request({
      type: 'POST',
      uri: 'Consumer/CompareGoals/AdditionalSavingsPlanImpact',
      urlParams: {
        clientId,
        fpId,
      },
      data: normalizedWhatIfs,
      sendStringifyJSON: true,
      showSpinner: false,
    })
    .then(response => {
      if (response.success) {
        const {
          WhatIfsPlanImpacts,
          AvailableCashflow,
          MaxCashflow,
          financialPlan,
        } = response.payload.viewData;

        dispatch(setErrors(clientId, []));
        dispatch(
          updateCompareGoalsPlanImpacts(
            WhatIfsPlanImpacts,
            clientId,
            goalId,
            whatIfKey,
            whatIfType,
            AvailableCashflow,
            MaxCashflow,
          ),
        );
        dispatch(
          updateWhatIfsData(clientId, goalId, whatIfType, {
            Options: {
              AvailableCashflow,
              MaxCashflow,
              TotalAllocatedCashflowOtherGoals: normalizedWhatIfs.reduce((accumulator, current) => {
                if (current.WhatIfKey === whatIfKey) {
                  return accumulator;
                }
                return accumulator + Math.round(+current.AllocatedCashflow);
              }, 0),
            },
          }),
        );
        dispatch(setPlanImpact(clientId, financialPlan));
      } else {
        dispatch(setErrors(clientId, response.errors));
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      () => dispatch(releasePopupSpinnerLoading(clientId)),
      () => dispatch(releasePopupSpinnerLoading(clientId)),
    );
};

export const applyAdditionalSavingsIfDirty = (clientId, fpId, whatIfs) => dispatch => {
  const { ADV } = window;
  const normalizedWhatIfs = whatIfs
    .filter(whatIf => !isEqual(whatIf.WhatIfPanelItem, whatIf.WhatIfInitialPanelItem))
    .map(whatIf => whatIf.WhatIfPanelItem.WhatIf);
  if (normalizedWhatIfs.length === 0) {
    return;
  }
  dispatch(acquirePopupSpinnerLoading(clientId));
  ADV.util
    .request({
      type: 'POST',
      uri: 'Consumer/CompareGoals/ApplyAdditionalSavings',
      urlParams: {
        clientId,
        fpId,
      },
      data: normalizedWhatIfs,
      sendStringifyJSON: true,
      showSpinner: false,
    })
    .then(response => {
      if (response.success) {
        dispatch(setErrors(clientId, []));
      } else {
        dispatch(setErrors(clientId, response.errors));
      }
    })
    .catch(() => {
      dispatch(setErrors(clientId, [ADV.settings.MsgLib.generic_error]));
    })
    .then(
      () => dispatch(releasePopupSpinnerLoading(clientId)),
      () => dispatch(releasePopupSpinnerLoading(clientId)),
    );
};
